import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../context/AuthContext';
import FilterPanel from '../components/FilterPanel';
import NavTabs from '../components/NavTabs';
import FacilityCard from '../components/FacilityCard';
import SubscriptionBanner from '../components/SubscriptionBanner';
import NotificationBell from '../components/NotificationBell';
import UpgradeModal from '../components/UpgradeModal';
import { getFullFacilities, getCounties, getLastSync } from '../api';
import { useDarkMode } from '../context/DarkModeContext';

const DEFAULT_FILTERS = {
  county: '',
  population: 'both',
  service_type: '',
  setting: '',
  search: '',
  psych_meds: false,
  mat: false,
  ppw: false,
  available_beds: false,
  zip: '',
  radius: '25',
};

function buildParams(filters) {
  const params = {};
  if (filters.search)                params.search       = filters.search;
  if (filters.county)                params.county       = filters.county;
  if (filters.population !== 'both') params.population   = filters.population;
  if (filters.service_type)          params.service_type = filters.service_type;
  if (filters.setting !== '')        params.setting      = filters.setting;
  if (filters.psych_meds)            params.psych_meds      = 'true';
  if (filters.mat)                   params.mat             = 'true';
  if (filters.ppw)                   params.ppw             = 'true';
  if (filters.available_beds)        params.available_beds  = 'true';
  const zipClean = (filters.zip || '').replace(/\D/g, '');
  if (zipClean.length === 5) {
    params.zip    = zipClean;
    params.radius = filters.radius || '25';
  }
  return params;
}

function Spinner() {
  return (
    <div className="flex justify-center py-16">
      <svg className="animate-spin w-8 h-8 text-blue-500" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
      </svg>
    </div>
  );
}

function EmptyState({ hasFilters }) {
  return (
    <div className="flex flex-col items-center py-20 text-center">
      <svg className="w-14 h-14 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
          d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      <p className="text-gray-500 font-medium text-base">No facilities match your filters</p>
      {hasFilters && (
        <p className="text-gray-400 text-sm mt-1">Try broadening your search criteria</p>
      )}
    </div>
  );
}

export default function Search() {
  const { isAuthenticated, hasFullAccess, isAdmin, logout, ready } = useAuth();
  const { dark, toggle: toggleDark } = useDarkMode();
  const navigate = useNavigate();

  const handleSignOut = () => {
    logout();
    navigate('/');
  };

  const [filters, setFilters]               = useState(DEFAULT_FILTERS);
  const [counties, setCounties]             = useState([]);
  const [facilities, setFacilities]         = useState([]);
  const [loading, setLoading]               = useState(true);
  const [error, setError]                   = useState('');
  const [sidebarOpen, setSidebarOpen]       = useState(false);
  const [lastSync, setLastSync]             = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    getCounties().then(setCounties).catch(() => {});
    getLastSync().then((d) => setLastSync(d.last_sync)).catch(() => {});
  }, []);

  const fetchFacilities = useCallback((currentFilters) => {
    setLoading(true);
    setError('');
    let cancelled = false;
    getFullFacilities(buildParams(currentFilters))
      .then((data) => { if (!cancelled) setFacilities(Array.isArray(data) ? data : []); })
      .catch(() => {
        if (!cancelled) {
          setError('Could not load facilities. Make sure the server is running.');
          setFacilities([]);
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!ready) return;
    const cancel = fetchFacilities(filters);
    return cancel;
  }, [ready, filters, isAuthenticated, fetchFacilities]);

  const hasFilters =
    filters.search !== '' ||
    filters.county !== '' ||
    filters.population !== 'both' ||
    filters.service_type !== '' ||
    filters.setting !== '' ||
    filters.psych_meds ||
    filters.mat ||
    filters.ppw;

  const handleFilterChange = (newFilters) => {
    // Intercept ZIP entry for non-individual tiers (guest and community can't use radius search)
    const newZip = (newFilters.zip || '').replace(/\D/g, '');
    const oldZip = (filters.zip || '').replace(/\D/g, '');
    if (newZip.length === 5 && oldZip.length < 5 && !hasFullAccess && !isAdmin) {
      setShowUpgradeModal(true);
      return;
    }
    setFilters(newFilters);
    setSidebarOpen(false);
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Helmet>
        <title>WA BHA Treatment Finder | Washington Behavioral Health Facility Directory</title>
        <meta name="description" content="Search Washington State behavioral health treatment facilities by county, service type, and population. Filter by mental health, SUD, inpatient, outpatient, and detox." />
      </Helmet>
      {/* Header */}
      <header className="bg-blue-700 text-white px-6 py-4 shadow sticky top-0 z-30">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl md:text-2xl font-bold leading-tight">
              WA BHA Treatment Finder
            </h1>
            <p className="text-blue-200 text-xs md:text-sm mt-0.5">
              Washington State behavioral health treatment facility directory
            </p>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            {/* Mobile filter toggle */}
            <button
              onClick={() => setSidebarOpen((o) => !o)}
              className="md:hidden flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium px-3 py-1.5 rounded-lg"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L13 13.414V19a1 1 0 01-.553.894l-4 2A1 1 0 017 21v-7.586L3.293 6.707A1 1 0 013 6V4z" />
              </svg>
              Filters
              {hasFilters && (
                <span className="bg-white text-blue-700 text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center">!</span>
              )}
            </button>

            {/* Mobile auth — visible on mobile only */}
            {!isAuthenticated && (
              <Link
                to="/login"
                className="md:hidden text-white text-sm font-semibold px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
              >
                Sign in
              </Link>
            )}

            {/* Dark mode toggle */}
            <button
              onClick={toggleDark}
              title={dark ? 'Switch to light mode' : 'Switch to dark mode'}
              className="p-1.5 rounded-lg text-blue-200 hover:text-white hover:bg-blue-600 transition-colors"
            >
              {dark ? (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707M17.657 17.657l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ) : (
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                    d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                </svg>
              )}
            </button>

            {/* Desktop nav */}
            <nav className="hidden md:flex items-center gap-4 text-sm">
              {isAuthenticated && hasFullAccess && <NotificationBell />}
              {isAuthenticated ? (
                <>
                  {isAdmin && (
                    <Link to="/admin" className="text-blue-200 hover:text-white underline underline-offset-2">
                      Admin
                    </Link>
                  )}
                  <button
                    onClick={handleSignOut}
                    className="text-blue-200 hover:text-white transition-colors"
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-blue-200 hover:text-white underline underline-offset-2">
                    Sign in
                  </Link>
                  <Link
                    to="/subscribe"
                    className="bg-white text-blue-700 hover:bg-blue-50 font-semibold px-3 py-1.5 rounded-lg transition-colors"
                  >
                    Subscribe
                  </Link>
                </>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Upgrade modal for ZIP radius (shown to guest and community tiers) */}
      {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} />}

      {/* Subscription upsell banner (guests only) */}
      <SubscriptionBanner />

      <NavTabs />

      {/* Search bar */}
      <div className="max-w-7xl mx-auto px-4 pt-4">
        <input
          type="text"
          value={filters.search}
          onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))}
          placeholder="Search by name, city, or keyword…"
          className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2.5 text-sm text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </div>

      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <div className="max-w-7xl mx-auto px-4 py-6 flex gap-6 items-start">
        {/* Sidebar */}
        <aside
          className={`
            fixed md:sticky top-0 md:top-20 left-0 h-full md:h-auto z-50 md:z-auto
            w-72 md:w-64 flex-shrink-0
            bg-white md:bg-transparent
            transform transition-transform duration-200
            ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          `}
        >
          <div className="flex items-center justify-between px-4 pt-5 pb-3 border-b border-gray-100 dark:border-gray-700 md:hidden">
            <span className="font-semibold text-gray-800 dark:text-gray-200">Filters</span>
            <button onClick={() => setSidebarOpen(false)} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="md:bg-white md:dark:bg-gray-800 md:rounded-xl md:shadow-sm md:border md:border-gray-200 md:dark:border-gray-700 p-4 md:p-5 overflow-y-auto h-full md:max-h-[calc(100vh-5.5rem)]">
            <h2 className="hidden md:block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-4">Filters</h2>

            <>
              <FilterPanel
                filters={filters}
                onChange={handleFilterChange}
                counties={counties}
              />
              {hasFilters && (
                <button
                  onClick={() => setFilters(DEFAULT_FILTERS)}
                  className="mt-5 w-full text-xs text-gray-500 hover:text-blue-600 underline underline-offset-2 text-left"
                >
                  Clear all filters
                </button>
              )}
            </>

            {/* Mobile nav links */}
            <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700 space-y-3 md:hidden">
              {isAuthenticated ? (
                <>
                  {isAdmin && (
                    <Link to="/admin" className="block text-sm text-blue-600 underline">Admin</Link>
                  )}
                </>
              ) : (
                <>
                  <Link to="/login" className="block text-sm text-blue-600 underline">Sign in</Link>
                  <Link to="/subscribe" className="block text-sm text-blue-600 underline">Subscribe</Link>
                </>
              )}
              {/* Resources — mobile */}
              <div className="pt-3 border-t border-gray-100 dark:border-gray-700">
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Resources</p>
                <Link to="/faq" className="block text-sm text-blue-600 dark:text-blue-400 underline">FAQ &amp; Help</Link>
                <Link to="/why" className="block text-sm text-blue-600 dark:text-blue-400 underline mt-1.5">What makes this different?</Link>
                <span className="block text-sm text-gray-400 dark:text-gray-500 mt-1.5 cursor-default">Educational Resources <span className="text-xs">(coming soon)</span></span>
              </div>
            </div>
          </div>

          {/* Resources panel — desktop only */}
          <div className="hidden md:block mt-4 bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 px-5 py-4">
            <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">Resources</h2>
            <ul className="space-y-2">
              <li>
                <Link
                  to="/faq"
                  className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                >
                  <svg className="w-4 h-4 flex-shrink-0 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  FAQ &amp; Help
                </Link>
              </li>
              <li>
                <Link
                  to="/why"
                  className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                >
                  <svg className="w-4 h-4 flex-shrink-0 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  What makes this different?
                </Link>
              </li>
              <li>
                <span className="flex items-center gap-2 text-sm text-gray-400 dark:text-gray-500 cursor-default">
                  <svg className="w-4 h-4 flex-shrink-0 text-gray-300 dark:text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  Educational Resources
                  <span className="text-xs text-gray-300 dark:text-gray-600">(coming soon)</span>
                </span>
              </li>
            </ul>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0">
          <div className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            {error ? (
              <p className="text-sm text-red-600">{error}</p>
            ) : loading ? (
              <p className="text-sm text-gray-400 dark:text-gray-500">Loading…</p>
            ) : (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Showing{' '}
                <span className="font-semibold text-gray-900 dark:text-gray-100">{facilities.length}</span>{' '}
                {facilities.length === 1 ? 'facility' : 'facilities'}
                {hasFilters && ' matching your filters'}
              </p>
            )}
            {lastSync && (
              <span className="text-xs text-gray-400 dark:text-gray-500 whitespace-nowrap">
                Data last updated{' '}
                {new Date(lastSync).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
            )}
          </div>

          {loading ? (
            <Spinner />
          ) : facilities.length === 0 && !error ? (
            <EmptyState hasFilters={hasFilters} />
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {facilities.map((f) => (
                <FacilityCard key={f.id} facility={f} />
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
